import { ref, computed, nextTick } from 'vue'
import { Table } from '@dacr/ui'
import { TableTypes } from '@/shared/types'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'

type ContextMenuType =
  | 'FileContextMenu'
  | 'MultipleFilesContextMenu'
  | 'FolderContextMenu'
  | 'MixedItemsContextMenu'

export function useExplorerContextMenu(
  isProcessingFile: (item: Item) => boolean
) {
  const table = Table.useTable(TableTypes.Explorer)

  const contextMenuComponent = ref<ContextMenuType | null>(null)
  const contextMenuRef = ref<HTMLDivElement | null>(null)
  const componentRef = ref<{ $el: HTMLElement } | null>(null)
  const contextMenuPos = ref({ x: 0, y: 0 })

  const contextMenuStyles = computed(() => ({
    top: `${contextMenuPos.value.y}px`,
    left: `${contextMenuPos.value.x}px`,
  }))

  const isFileContextMenu = computed(
    () => contextMenuComponent.value === 'FileContextMenu'
  )
  const isMultipleFilesContextMenu = computed(
    () => contextMenuComponent.value === 'MultipleFilesContextMenu'
  )
  const isFolderContextMenu = computed(
    () => contextMenuComponent.value === 'FolderContextMenu'
  )
  const isMixedItemsContextMenu = computed(
    () => contextMenuComponent.value === 'MixedItemsContextMenu'
  )

  const closeContextMenu = () => {
    contextMenuComponent.value = null
  }

  const handleWindowClick = () => {
    closeContextMenu()
    window.document.body.style.pointerEvents = 'initial'
    window.removeEventListener('click', handleWindowClick)
  }

  const selectedRowsAreAllFiles = () => {
    const items = table.selectedRows.value
    return items.length > 0
  }

  const resolveMenuType = (
    allFiles: boolean,
    allFolders: boolean,
    singleFile: boolean
  ): ContextMenuType => {
    if (singleFile && allFiles) return 'FileContextMenu'
    if (allFiles) return 'MultipleFilesContextMenu'
    if (allFolders) return 'FolderContextMenu'
    return 'MixedItemsContextMenu'
  }

  const positionMenu = async (clientX: number, clientY: number) => {
    await nextTick()

    const menuHeight = contextMenuRef.value?.clientHeight || 0
    const menuWidth = contextMenuRef.value?.clientWidth || 0

    const topOfMenu = clientY - menuHeight
    const bottomOfMenu = clientY + menuHeight

    const topValue =
      bottomOfMenu >= window.innerHeight
        ? topOfMenu <= 0
          ? 0 - topOfMenu
          : topOfMenu
        : clientY

    const rightBound =
      componentRef.value?.$el.getBoundingClientRect().right ?? window.innerWidth

    if (clientX + menuWidth >= rightBound) {
      contextMenuPos.value.x = clientX - menuWidth
    } else {
      contextMenuPos.value.x = clientX
    }

    contextMenuPos.value.y = topValue
  }

  const handleRightClick = async (
    event: MouseEvent,
    row: Item,
    items: Item[]
  ) => {
    event.preventDefault()

    if (isProcessingFile(row)) return

    window.document.body.style.pointerEvents = 'none'
    window.addEventListener('click', handleWindowClick)

    if (table.selectedRows.value.length) {
      if (!table.isRowSelected(row.id)) {
        table.resetSelection()
        table.toggleRow(row.id)
      }
    } else {
      table.toggleRow(row.id)
    }

    const selectedItems = items.filter((item) => table.isRowSelected(item.id))
    const allFiles = selectedItems.every(isFile)
    const allFolders = selectedItems.every(isFolder)
    const singleFile = table.selectedRows.value.length === 1

    contextMenuComponent.value = resolveMenuType(
      allFiles,
      allFolders,
      singleFile
    )

    await positionMenu(event.clientX, event.clientY)
  }

  const handleContextMenuClick = async (
    event: MouseEvent,
    row: Item,
    items: Item[]
  ) => {
    event.preventDefault()

    if (isProcessingFile(row)) return

    if (!table.isRowSelected(row.id)) {
      table.toggleRow(row.id)
    }

    window.document.body.style.pointerEvents = 'none'
    window.addEventListener('click', handleWindowClick)

    const selectedItems = items.filter((item) => table.isRowSelected(item.id))
    const allFiles = selectedItems.every(isFile)
    const allFolders = selectedItems.every(isFolder)
    const singleFile = table.selectedRows.value.length === 1

    contextMenuComponent.value = resolveMenuType(
      allFiles,
      allFolders,
      singleFile
    )

    await positionMenu(event.clientX, event.clientY)
  }

  return {
    contextMenuComponent,
    contextMenuRef,
    componentRef,
    contextMenuPos,
    contextMenuStyles,
    isFileContextMenu,
    isMultipleFilesContextMenu,
    isFolderContextMenu,
    isMixedItemsContextMenu,
    closeContextMenu,
    handleRightClick,
    handleContextMenuClick,
  }
}
